import { API } from "@swivl-mono/api-helper";
import { warn } from "@swivl-mono/helpers";
import { UserInterface, UserPropKey } from "@swivl-mono/types";
import { channel } from "diagnostics_channel";

import { Member } from "../../Member/Member.Current";
import NotificationsModel from "../../Notifications/Notifications.Model";
import PendingUpdatesModel, { PendingUpdateType } from "../../Pending/Pending.Model";
import { UsersModel } from "../Users.Model";

const NUMBER_OF_USERS_PER_PAGE = 100;

export const UsersModelUsersMethods = {
  loadUsersByIds: async (ids: number[]) => {
    let allUsers = { ...UsersModel.state.users };

    if (!ids.length) {
      return;
    }
    const idsNotLoaded = ids.filter((id) => !allUsers[id]);

    if (!idsNotLoaded.length) {
      return;
    }

    API.Users.getUsersById(Member.orgId!, idsNotLoaded)
      .then((users) => {
        UsersModel.setUserSessionOrEscalation(users, undefined, undefined);
      })
      .catch((error) => {
        warn("Error loading loadUsersByIds", error);
      });
  },

  getUserById: async (id: number): Promise<UserInterface | undefined> => {
    if (UsersModel.state.users && UsersModel.state.users[id]) {
      return UsersModel.state.users[id];
    }
    let user = await API.Users.getUserById(id);
    if (!user) {
      return undefined;
    }
    UsersModel.setUserSessionOrEscalation([user], undefined, undefined);
    return user;
  },
  updateUser: async (user: Partial<UserInterface>, save = true) => {
    if (!user || !user.id) {
      warn("No user to update", user);
      return;
    }

    if (save) {
      API.Users.updateUser(Member.orgId!, user.id, user).then((updatedUser) => {
        UsersModel.setUserSessionOrEscalation([updatedUser], undefined, undefined);
      });
    } else {
      const users = { ...UsersModel.state.users };
      const updatedUser = Object.assign({}, users[user.id], user);
      UsersModel.setUserSessionOrEscalation([updatedUser], undefined, undefined);
    }
  },
  searchForUser: async (searchTerm: string): Promise<UserInterface[]> => {
    return API.Users.searchForUsers(Member.orgId!, searchTerm);
  },

  loadUsers: async (page?: number) => {
    if (UsersModel.state.isLoadingMoreUsers) {
      return;
    }
    if (!page) {
      page = UsersModel.state.loadedUsersPage + 1;
    }
    if (UsersModel.state.loadedUsersPage >= page) {
      return;
    }
    UsersModel.internalSetState({ isLoadingMoreUsers: true });
    const users = await API.Users.getRecentlyActiveUsers(Member.orgId!, NUMBER_OF_USERS_PER_PAGE, NUMBER_OF_USERS_PER_PAGE * page);
    let numberOfUnarchivedUsers = 0;
    users.forEach((user) => {
      if (!user.isArchived) {
        numberOfUnarchivedUsers++;
      }
    });

    UsersModel.setUserSessionOrEscalation(users, undefined, undefined, {
      moreUsersToLoad: users.length >= NUMBER_OF_USERS_PER_PAGE,
      loadedUsersPage: page,
      isLoadingMoreUsers: false,
    });
    if (numberOfUnarchivedUsers > 50) {
      UsersModel.loadUsers(page + 1);
    }
  },
  loadNextPageOfArchivedUsers: () => {
    UsersModel.loadUsers(UsersModel.state.loadedUsersPage + 1);
  },

  getUsersByPhoneNumber: (phone: string) => {
    return API.Users.getUsersByPhone(Member.orgId!, phone);
  },
  updateSingleUser: (userId: number, userData: Partial<UserInterface>) => {
    if (!userData || !userData.id) {
      warn("No user to update", userData);
      return;
    }
    const users = { ...UsersModel.state.users };
    if (!users[userId]) {
      warn("User not found in state...loading", userId);
      UsersModel.loadUsersByIds([userId]);
      return;
    }
    const user = users[userId];
    let isDirty = false;
    for (const [key, value] of Object.entries(userData)) {
      if (user[key as keyof UserInterface] !== value) {
        isDirty = true;
        (user[key as keyof UserInterface] as any) = value;
        break;
      }
    }
    if (!isDirty) {
      warn("No changes detected for user update", userId, userData);
      return;
    }
    UsersModel.setUserSessionOrEscalation([user], undefined, undefined);
  },
  setArchivedForUser: (userId: number | undefined, archived: boolean) => {
    if (!userId) {
      warn("No user ID provided to setArchivedForUser");
      return;
    }
    PendingUpdatesModel.addPendingUpdate(PendingUpdateType.USER_ARCHIVED_STATE, userId);

    API.Users.setArchivedForUser(Member.orgId!, userId, archived)
      .then((user) => {
        UsersModel.setUserSessionOrEscalation([user]);
        PendingUpdatesModel.removePendingUpdate(PendingUpdateType.USER_ARCHIVED_STATE, userId);
      })
      .catch((error) => {
        warn("Error setting archived status for user", error);
        PendingUpdatesModel.removePendingUpdate(PendingUpdateType.USER_ARCHIVED_STATE, userId);
        NotificationsModel.createToastNotification(`There was an error ${archived ? "closing" : "opening"} this user.`);
      });
  },
};
